import React, { useEffect } from 'react';
import Footer from '../components/Footer';
import { getHostname } from '../utils/getHostname';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PricingTable } from '../components/PricingTable';

// Hero Banner Component
function BannerSlider() {
  return (
    <section className="privacy-hero">
      <div className="container">
        <img src="/images/logo.svg" alt="" className="privacy-hero-icon" aria-hidden="true" />
        <h1>Browse in complete privacy</h1>
        <p>
          Stay invisible online with military-grade encryption, zero-log policy,
          and true incognito protection — your identity stays hidden, always.
        </p>
        <a href="/signup" className="custom-btn">Go Incognito Now</a>
      </div>
    </section>
  );
}


// Why Choose Us Component
function WhyChooseUs() {
  const features = [
    { icon: 'icons-01.svg', text: 'Anonymous Browsing' },
    { icon: 'icons-02.svg', text: 'Hidden IP Address' },
    { icon: 'icons-03.svg', text: 'Zero Trace Logs' },
    { icon: 'icons-04.svg', text: 'No Activity Tracking' },
    { icon: 'icons-05.svg', text: '256-bit Encryption' },
    { icon: 'icons-06.svg', text: 'Cancel Anytime' }
  ];

  return (
    <section className="bg-gray feature-grid-section">
      <div className="container">
        <div id="WhyChooseUs" className="why-choose">
          <h2 className="title-head">Built for privacy</h2>
          <div className="row">
            {features.map((feature, index) => (
              <div key={index} className="col-4 col-xl-2 privacy-feature-col">
                <div className="inside">
                  <img src={`/images/icons/${feature.icon}`} alt="optimised" />
                  <p>{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// VPN Content Sections
function VPNContent() {
  return (
    <section className="bg-gray">
      <div className="container">
        <div className="why-vpn">
          <div className="row privacy-block-dark">
            <div className="col-md-6">
              <img style={{padding: '70px'}} src="/assets/images/vpn-content/anonymous.png" alt="Anonymous browsing" />
            </div>
            <div className="col-md-6 vpn-content-text privacy-block-text" style={{paddingTop: '120px'}}>
              <strong style={{fontSize: '28px'}}>Stay completely anonymous</strong>
              <br /><br />
              <p className="vpn-content-paragraph" style={{fontSize: '18px'}}>Mask your real IP address and browse as if you were never there.
                Websites, advertisers, and trackers see nothing — your digital footprint disappears
                the moment you connect.</p>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 privacy-block-text" style={{paddingTop: '120px'}}>
              <strong style={{fontSize: '28px'}}>Your data stays invisible.</strong>
              <br /><br />
              <p style={{fontSize: '18px'}}>Public Wi-Fi, hotel networks, coffee shop hotspots — they all expose you.
                Our encryption wraps every packet in a layer of privacy so hackers, ISPs, and
                snoopers can't see what you're doing.</p>
            </div>
            <div className="col-md-6">
              <img style={{padding: '70px'}} src="/assets/images/vpn-content/internet.png" alt="Encrypted connection" />
            </div>
          </div>
          <div className="row privacy-block-accent">
            <div className="col-md-6">
              <img style={{padding: '70px'}} src="/assets/images/vpn-content/network.png" alt="No logs policy" />
            </div>
            <div className="col-md-6 vpn-content-text privacy-block-text" style={{paddingTop: '120px'}}>
              <strong style={{fontSize: '28px'}}>Zero logs. Zero traces.</strong>
              <br /><br />
              <p className="vpn-content-paragraph" style={{fontSize: '18px'}}>We don't record your browsing history, connection timestamps, or IP addresses.
                True incognito mode isn't a feature — it's our foundation. What you do online
                is yours alone.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Platforms Component
function Platforms() {
  const platforms = [
    { icon: '005-windows.png', name: 'Windows', delay: 'delay-250' },
    { icon: '001-apple-logo.png', name: 'macOS', delay: 'delay-500' },
    { icon: '004-linux.png', name: 'Linux', delay: 'delay-750' },
    { icon: '002-apple.png', name: 'iOS', delay: 'delay-1000' },
    { icon: '003-android.png', name: 'Android', delay: 'delay-1250' },
    { icon: '006-chrome.png', name: 'Chrome', delay: 'delay-1500' }
  ];

  return (
    <section className="bg-gray feature-grid-section">
      <div className="container">
        <div id="Platforms" className="why-choose">
          <h2 className="title-head">Available on Platforms</h2>
          <div className="row">
            {platforms.map((platform, index) => (
              <div key={index} className="col-4 col-xl-2 platform-feature-col">
                <div className="inside">
                  <img 
                    src={`/assets/images/platforms/${platform.icon}`} 
                    alt={platform.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.error(`Failed to load image: /assets/images/platforms/${platform.icon}`);
                    }}
                  />
                  <p>{platform.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Pricing section uses shared PricingTable component
function Pricing() {
  return (
    <div className="pricing-table animatedParent">
      <div className="container">
        <PricingTable
          title="Choose Your Plan"
          variant="home"
        />
      </div>
    </div>
  );
}

// Info Pricing Component
function InfoPricing() {
  return (
    <div className="container">
      <div className="info-pricing">
        <h4 style={{marginBottom: '15px'}}>One click to go completely incognito</h4>
        <p style={{paddingTop: '5px', paddingBottom: '40px'}}>Our apps are built around a single promise: your online life is nobody else's business.
          Military-grade encryption, a strict no-logs policy, and one-tap connection — privacy
          shouldn't require a technical degree.</p>
        <ul>
          <li>
            <h6>Strictly No Logs</h6>
            <p>We never record browsing activity, connection timestamps, or DNS queries. Your sessions
              leave no trace on our servers — because true privacy means we literally have nothing
              to hand over, even if asked.</p>
          </li>
          <li className="right">
            <h6>Incognito by Default</h6>
            <p>Every connection automatically masks your IP, encrypts your traffic, and blocks
              WebRTC leaks. You don't configure privacy — it's always on, from the moment
              you tap connect.</p>
          </li>
        </ul>
        <ul className="right">
          <li>
            <h6>Fast &amp; Unthrottled</h6>
            <p>Privacy shouldn't mean slow. Our global network delivers top-tier speeds with
              no bandwidth caps and no throttling — browse, stream, and download at full speed
              while staying completely hidden.
            </p>
          </li>
          <li className="button right">
            <a href="/signup" className="custom-btn">Start Browsing Privately</a>
          </li>
        </ul>
      </div>
    </div>
  );
}

function TestimonialStars() {
  return (
    <div className="testimonial-stars" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

// User Slider/Testimonials Component
function UserSlider() {
  const hostname = getHostname();
  const testimonials = [
    {
      text: "Finally a VPN that actually respects privacy. No logs, no tracking — I feel genuinely invisible online. This is the one to get.",
      name: "Sanjib Lamsal",
      title: "Software Engineer"
    },
    {
      text: "I use it on every device. It runs silently in the background and I never worry about who's watching my traffic. Highly recommended.",
      name: "Biek Gurung",
      title: "Computer Programmer"
    },
    {
      text: `${hostname} keeps me anonymous wherever I go. My ISP can't see what I'm browsing and neither can anyone else on public Wi-Fi.`,
      name: "Rabin Acharya",
      title: "Freelance Developer"
    },
    {
      text: "If you care about staying private online, this is non-negotiable. One click and you're in incognito mode — everywhere.",
      name: "Tirtha Raj Pokharel",
      title: "Computer Engineer"
    }
  ];

  return (
    <section className="testimonials-section user-slider">
      <div className="container">
        <div className="testimonials-header">
          <h2 className="title-head">What our users say</h2>
          <p className="testimonials-subtitle">
            Trusted by privacy-conscious users who refuse to be tracked
          </p>
        </div>
        <div className="slider owl-carousel owl-theme testimonials-carousel">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="item">
              <article className="testimonial-card">
                <TestimonialStars />
                <blockquote className="testimonial-quote">
                  <p>{testimonial.text}</p>
                </blockquote>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.title}</span>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Main Home Component
function Home() {
  useDocumentTitle('Home');
  
  useEffect(() => {
    // Load jQuery and other scripts
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const initializeScripts = async () => {
      try {
        // Load scripts in order
        await loadScript('/assets/js/lib/jquery.js');
        await loadScript('https://stackpath.bootstrapcdn.com/bootstrap/4.1.1/js/bootstrap.min.js');
        await loadScript('/assets/js/lib/owl.carousel.min.js');
        await loadScript('/assets/js/lib/css3-animate-it.js');
        await loadScript('/assets/js/lib/counter.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/moment.js/2.22.0/moment.min.js');
        await loadScript('/vendor/vex/js/vex.combined.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/card/2.4.0/card.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/bootstrap-select/1.12.4/js/bootstrap-select.min.js');
        await loadScript('/assets/js/chosen_v1.8.7/chosen.jquery.min.js');
        await loadScript('/assets/js/main.js');
        await loadScript('/assets/js/next.js?v=2');
        await loadScript('/assets/js/validatecc.js');
        await loadScript('/assets/js/axn.js');

        // Wait a bit for DOM to be ready
        setTimeout(() => {
          if (window.jQuery && window.jQuery.fn.owlCarousel) {
            // Initialize base slider
            const $baseSlider = window.jQuery('.base-slider.owl-carousel');
            if ($baseSlider.length) {
              $baseSlider.owlCarousel({
                items: 1,
                loop: true,
                autoplay: true,
                autoplayTimeout: 5000,
                nav: false,
                dots: true
              });
            }

            // Initialize user slider
            const $userSlider = window.jQuery('.user-slider .slider.owl-carousel');
            if ($userSlider.length) {
              $userSlider.owlCarousel({
                items: 1,
                loop: true,
                autoplay: true,
                autoplayTimeout: 5000,
                nav: false,
                dots: true
              });
            }
          }

          // The css3-animate-it library initializes automatically when loaded
          // Animations work with CSS classes (animated, bounceInUp, etc.)
        }, 100);
      } catch (error) {
        console.error('Error loading scripts:', error);
      }
    };

    initializeScripts();
  }, []);

  return (
    <>
      <BannerSlider />
      <WhyChooseUs />
      <VPNContent />
      <Platforms />
      <Pricing />
      <InfoPricing />
      <UserSlider />
      <Footer />
    </>
  );
}

export default Home;
